import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight, PieChart as PieChartIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authFetch = (url, options = {}) => fetch(url, { credentials: "include", ...options });

const RANGES = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 Days" },
  { id: "30d", label: "30 Days" },
  { id: "1y", label: "1 Year" },
  { id: "all", label: "All Time" },
  { id: "daily", label: "Daily" },
];

const COLORS = ["#B8860B", "#6B4423", "#D9B96A", "#8a6408", "#57534E", "#a16207", "#d6cfc4", "#44403c", "#eab308", "#78716c"];

const dayLabel = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

function Donut({ sources, total }) {
  const r = 70;
  const C = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg viewBox="0 0 200 200" className="w-56 h-56 sm:w-64 sm:h-64" data-testid="sources-pie-chart">
      {sources.map((s, i) => {
        const frac = s.count / total;
        const dash = `${Math.max(frac * C - 1.5, 0.5)} ${C}`;
        const rot = acc * 360 - 90;
        acc += frac;
        return (
          <circle
            key={s.source}
            cx="100" cy="100" r={r}
            fill="none"
            stroke={COLORS[i % COLORS.length]}
            strokeWidth="34"
            strokeDasharray={dash}
            transform={`rotate(${rot} 100 100)`}
          />
        );
      })}
      <text x="100" y="95" textAnchor="middle" fontSize="26" fontWeight="700" fill="#1C1917" fontFamily="Playfair Display, serif">{total}</text>
      <text x="100" y="116" textAnchor="middle" fontSize="11" fill="#78716c" letterSpacing="2">VISITORS</text>
    </svg>
  );
}

function Legend({ sources, total }) {
  return (
    <div className="flex-1 w-full divide-y divide-amber-600/10" data-testid="sources-legend">
      {sources.map((s, i) => (
        <div key={s.source} className="py-3 flex items-center gap-3" data-testid={`source-row-${s.source.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <span className="w-3 h-3 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
          <span className="text-sm font-semibold text-stone-800 flex-1">{s.source}</span>
          <span className="text-sm text-stone-500">{s.count} visit{s.count === 1 ? "" : "s"}</span>
          <span className="text-sm font-bold text-amber-800 w-14 text-right">{Math.round((s.count / total) * 100)}%</span>
        </div>
      ))}
    </div>
  );
}

function DailyBars({ days, selected, onSelect }) {
  const [startIdx, setStartIdx] = useState(() => Math.max(0, days.length - 14));
  const window = 14;
  const visible = days.slice(startIdx, startIdx + window);
  const maxY = Math.max(1, ...days.map((d) => d.count));
  const W = 600;
  const H = 190;
  const padL = 30;
  const padB = 30;
  const padT = 14;
  const slotW = (W - padL - 8) / Math.max(visible.length, 1);
  const barW = Math.min(30, slotW * 0.6);

  useEffect(() => {
    setStartIdx((i) => Math.min(i, Math.max(0, days.length - window)));
  }, [days.length]);

  return (
    <div data-testid="daily-bar-chart">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setStartIdx((i) => Math.max(0, i - window))}
          disabled={startIdx === 0}
          data-testid="daily-prev-page"
          className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-stone-500 hover:text-amber-700 disabled:opacity-30 disabled:hover:text-stone-500 transition-colors duration-200"
        >
          <ChevronLeft className="w-4 h-4" /> Older
        </button>
        <span className="text-xs text-stone-400 font-medium">
          {visible.length ? `${dayLabel(visible[0].date)} – ${dayLabel(visible[visible.length - 1].date)}` : ""}
        </span>
        <button
          onClick={() => setStartIdx((i) => Math.min(Math.max(0, days.length - window), i + window))}
          disabled={startIdx >= Math.max(0, days.length - window)}
          data-testid="daily-next-page"
          className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-stone-500 hover:text-amber-700 disabled:opacity-30 disabled:hover:text-stone-500 transition-colors duration-200"
        >
          Newer <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const gy = padT + (1 - f) * (H - padT - padB);
          return (
            <g key={f}>
              <line x1={padL} y1={gy} x2={W - 8} y2={gy} stroke="#e7e0d4" strokeWidth="1" />
              <text x={padL - 6} y={gy + 4} textAnchor="end" fontSize="10" fill="#a8a29e">{Math.round(maxY * f)}</text>
            </g>
          );
        })}
        {visible.map((d, i) => {
          const cx = padL + slotW * i + slotW / 2;
          const bh = (d.count / maxY) * (H - padT - padB);
          const isSel = d.date === selected;
          return (
            <g key={d.date} onClick={() => onSelect(d.date)} className="cursor-pointer" data-testid={`daily-bar-${d.date}`}>
              <rect x={cx - slotW / 2} y={padT} width={slotW} height={H - padT - padB} fill="transparent" />
              <rect
                x={cx - barW / 2}
                y={H - padB - bh}
                width={barW}
                height={Math.max(bh, 2)}
                rx="3"
                fill={isSel ? "#B45309" : "#D9B96A"}
                className="transition-colors duration-200 hover:fill-amber-600"
              />
              <text x={cx} y={H - padB - bh - 6} textAnchor="middle" fontSize="10" fontWeight={isSel ? "700" : "500"} fill={isSel ? "#B45309" : "#78716c"}>
                {d.count > 0 ? d.count : ""}
              </text>
              <text x={cx} y={H - 8} textAnchor="middle" fontSize="10" fill={isSel ? "#B45309" : "#a8a29e"}>
                {new Date(`${d.date}T12:00:00`).toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="text-xs text-stone-400 mt-2 text-center">Tap a day to see where that day's visitors came from.</p>
    </div>
  );
}

export default function SourcesView({ onBack }) {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [days, setDays] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayData, setDayData] = useState(null);

  const isDaily = range === "daily";

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/admin/sources?range=${range}`);
    if (res.ok) {
      const d = await res.json();
      if (d.range === "daily") {
        setDays(d.days);
        const withVisits = [...d.days].reverse().find((x) => x.count > 0);
        const last = withVisits || d.days[d.days.length - 1] || null;
        setSelectedDay(last ? last.date : null);
      } else {
        setData(d);
      }
    }
  }, [range]);

  const loadDay = useCallback(async (date) => {
    if (!date) return;
    const res = await authFetch(`${API}/admin/sources?date=${date}`);
    if (res.ok) setDayData(await res.json());
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isDaily) loadDay(selectedDay); }, [isDaily, selectedDay, loadDay]);

  const total = data?.total ?? 0;
  const dayIndex = days ? days.findIndex((d) => d.date === selectedDay) : -1;
  const stepDay = (dir) => {
    const next = dayIndex + dir;
    if (days && next >= 0 && next < days.length) setSelectedDay(days[next].date);
  };

  return (
    <div data-testid="sources-page">
      <button
        onClick={onBack}
        data-testid="sources-back-btn"
        className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-stone-500 hover:text-amber-700 transition-colors duration-200 mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Stats
      </button>

      <h2 className="font-display text-2xl sm:text-3xl text-stone-900 mb-1">Where Visitors Come From</h2>
      <p className="text-sm text-stone-500 mb-6">Traffic source for every site visit.</p>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="sources-ranges">
        {RANGES.map((r) => (
          <button
            key={r.id}
            onClick={() => setRange(r.id)}
            data-testid={`sources-range-${r.id}`}
            className={`text-xs uppercase tracking-wider font-semibold px-4 py-2 rounded-full border transition-colors duration-300 ${
              range === r.id ? "bg-amber-600 text-white border-amber-600 shadow-md" : "border-amber-600/25 bg-white text-stone-500 hover:text-amber-800"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {isDaily ? (
        <>
          <div className="gold-ring-card rounded-xl p-6 sm:p-8 mb-6">
            {days && days.length > 0 ? (
              <DailyBars days={days} selected={selectedDay} onSelect={setSelectedDay} />
            ) : (
              <p className="text-sm text-stone-500 py-10 text-center" data-testid="daily-empty">No visits recorded yet. Check back once traffic arrives.</p>
            )}
          </div>

          {selectedDay && (
            <div className="gold-ring-card rounded-xl p-6 sm:p-8" data-testid="daily-detail">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => stepDay(-1)}
                  disabled={dayIndex <= 0}
                  data-testid="daily-prev-day"
                  className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-stone-500 hover:text-amber-700 disabled:opacity-30 disabled:hover:text-stone-500 transition-colors duration-200"
                >
                  <ChevronLeft className="w-4 h-4" /> Prev Day
                </button>
                <h3 className="font-display text-xl text-stone-900" data-testid="daily-detail-date">{dayLabel(selectedDay)}</h3>
                <button
                  onClick={() => stepDay(1)}
                  disabled={days && dayIndex >= days.length - 1}
                  data-testid="daily-next-day"
                  className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-stone-500 hover:text-amber-700 disabled:opacity-30 disabled:hover:text-stone-500 transition-colors duration-200"
                >
                  Next Day <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              {dayData && dayData.total > 0 ? (
                <div className="flex flex-col sm:flex-row items-center gap-8">
                  <Donut sources={dayData.sources} total={dayData.total} />
                  <Legend sources={dayData.sources} total={dayData.total} />
                </div>
              ) : (
                <p className="text-sm text-stone-500 py-10 text-center" data-testid="daily-detail-empty">No visits on this day.</p>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="gold-ring-card rounded-xl p-6 sm:p-8">
          {total > 0 ? (
            <div className="flex flex-col sm:flex-row items-center gap-8">
              <Donut sources={data.sources} total={total} />
              <Legend sources={data.sources} total={total} />
            </div>
          ) : (
            <div className="py-16 text-center">
              <PieChartIcon className="w-8 h-8 text-amber-700/40 mx-auto mb-3" />
              <p className="text-sm text-stone-500" data-testid="sources-empty">No visits in this period yet. Check back once traffic arrives.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
